import { ChangeDetectorRef } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DocumentService } from 'src/app/services/document.service';
import { DriveItemPresentationPolicy } from './drive-item-presentation.policy';
import { DriveWorkspaceState } from './drive-viewer.state';

export class DriveDateSectionLoader {
  constructor(
    private readonly workspace: DriveWorkspaceState,
    private readonly documents: DocumentService,
    private readonly sanitizer: DomSanitizer,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly presentation: DriveItemPresentationPolicy,
    private readonly translate: (key: string) => string,
    private readonly myDriveLabel: () => string
  ) {}

  loadByDate(): void {
    this.documents.getGroupedDocumentsByDate().subscribe({
      next: (documents) => {
        const root = this.workspace.items.find((item) => item.type === 'folder' && item.parentId == null);
        const rootName = root?.name || this.myDriveLabel();
        const rootId = root?.id || 'root';
        const byDateLabel = this.translate('documents.group_by_date') || 'Por fecha';
        this.ensureFolder('byDate', byDateLabel, [rootName, byDateLabel], rootId);
        const buckets = this.buckets();
        const now = Date.now();
        (documents || []).forEach((document: any, index: number) => {
          const createdAt = document?.createdAt ? Date.parse(document.createdAt) : now;
          const ageDays = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
          const bucket = buckets.find((candidate) => candidate.matches(ageDays)) || buckets[buckets.length - 1];
          const groupId = `g_${bucket.key}`;
          this.ensureFolder(groupId, bucket.label, [rootName, byDateLabel, bucket.label], 'byDate');
          this.workspace.items.push(this.toDateItem(document, index, groupId, [rootName, byDateLabel, bucket.label]));
        });
      },
      error: () => { /* Optional date grouping must not hide the main tree. */ }
    });
  }

  mergeRootDocuments(): void {
    const source = this.documents.getDocuments(undefined, 1, 1000);
    if (!source || typeof source.subscribe !== 'function') {
      return;
    }
    source.subscribe({
      next: (response) => {
        const documents = (response?.documents || []).filter((document: any) => {
          return document?.id && String(this.documentName(document)).trim();
        });
        const validIds = new Set(documents.map((document: any) => String(document.id)));
        const retained = this.workspace.items.filter((item) => {
          return item.type === 'folder' || !item.documentId || validIds.has(String(item.documentId));
        });
        const existing = new Set(retained.filter((item) => item.documentId).map((item) => String(item.documentId)));
        const root = retained.find((item) => item.type === 'folder' && item.parentId == null);
        const rootId = root?.id ?? 'root';
        const rootName = root?.name || this.myDriveLabel();
        const additions = documents
          .filter((document: any) => !existing.has(String(document.id)))
          .map((document: any) => ({
            id: String(document.id),
            name: this.documentName(document),
            type: this.presentation.typeByExtension(this.documentName(document)),
            owner: 'you' as const,
            modified: document.created_at ?? document.createdAt ?? new Date().toISOString(),
            size: 0,
            path: [rootName],
            parentId: rootId,
            documentId: String(document.id)
          }));
        if (additions.length || retained.length !== this.workspace.items.length) {
          this.workspace.items = [...retained, ...additions];
          this.changeDetector.detectChanges();
        }
      },
      error: () => { /* Best effort compatibility merge. */ }
    });
  }

  private toDateItem(document: any, index: number, parentId: string, path: string[]) {
    const filename = document?.originalFilename || 'documento';
    const type = this.presentation.typeByExtension(filename);
    const previewUrl = document?.publicUrl || '';
    const item: any = {
      id: String(document?.id || `doc_${parentId}_${index}`),
      name: filename,
      type,
      owner: 'you',
      modified: document?.createdAt || new Date().toISOString(),
      size: 0,
      shared: false,
      parentId,
      path,
      previewUrl,
      documentId: document?.id ? String(document.id) : undefined
    };
    if (previewUrl && !['pdf', 'txt'].includes(type) && this.presentation.extension(filename) !== 'csv') {
      item.previewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.presentation.viewerUrl(item, previewUrl)
      );
    }
    return item;
  }

  private ensureFolder(id: string, name: string, path: string[], parentId: string): void {
    if (!this.workspace.items.some((item) => item.id === id)) {
      this.workspace.items.push({
        id, name, type: 'folder', owner: 'you', modified: new Date().toISOString(), size: 0, path, parentId
      });
    }
  }

  private documentName(document: any): string {
    return document?.originalFilename ?? document?.original_filename ?? '';
  }

  private buckets() {
    return [
      { key: 'today', label: this.translate('documents.group_today') || 'Hoy', matches: (days: number) => days === 0 },
      { key: 'last3days', label: this.translate('documents.group_last3days') || 'Últimos 3 días', matches: (days: number) => days > 0 && days <= 3 },
      { key: 'lastWeek', label: this.translate('documents.group_lastWeek') || 'Última semana', matches: (days: number) => days > 3 && days <= 7 },
      { key: 'lastMonth', label: this.translate('documents.group_lastMonth') || 'Último mes', matches: (days: number) => days > 7 && days <= 30 },
      { key: 'older', label: this.translate('documents.group_older') || 'Más antiguos', matches: (days: number) => days > 30 }
    ];
  }
}
